import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Copy, IndianRupee } from "lucide-react";
import { fetchSevaById, createSevaPayment, fetchSevaPaymentStatus } from "@/api/sevas";
import { fetchGothras, fetchNakshatras, fetchZodiacSigns } from "@/api/sankalpa";
import { INSTITUTION_REFERENCE } from "@/lib/api";
import { MATHA } from "@/lib/constants";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STEP_KEYS = ["seva", "date", "devotee", "payment", "done"];
const EASEBUZZ_SCRIPT_SRC = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js";
const DEVOTEE_NAME_MAX_LENGTH = 80;
const DEVOTEE_PHONE_LENGTH = 10;
const DEVOTEE_ADDRESS_MAX_LENGTH = 250;

function validateDevoteeField(field, value, bookingText) {
  const trimmedValue = value.trim();

  if (field === "devotee_name") {
    if (!trimmedValue) return bookingText.errDevoteeNameRequired;
    if (trimmedValue.length < 2) return bookingText.errDevoteeNameShort;
    if (!/^[\p{L}\s.'-]+$/u.test(trimmedValue)) return bookingText.errDevoteeNameInvalid;
    return "";
  }

  if (field === "devotee_phone") {
    if (!trimmedValue) return bookingText.errDevoteePhoneRequired;
    if (!/^\d+$/.test(trimmedValue)) return bookingText.errDevoteePhoneInvalid;
    if (trimmedValue.length !== DEVOTEE_PHONE_LENGTH) return bookingText.errDevoteePhoneLength;
    return "";
  }

  if (field === "devotee_address") {
    if (!trimmedValue) return "";
    if (trimmedValue.length < 10) return bookingText.errDevoteeAddressShort;
    return "";
  }

  return "";
}

function validateDevoteeForm(form, bookingText) {
  return {
    devotee_name: validateDevoteeField("devotee_name", form.devotee_name, bookingText),
    devotee_phone: validateDevoteeField("devotee_phone", form.devotee_phone, bookingText),
    devotee_address: validateDevoteeField("devotee_address", form.devotee_address, bookingText),
  };
}

function hasDevoteeErrors(errors) {
  return Object.values(errors).some(Boolean);
}

function loadEasebuzzScript() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (typeof window.EasebuzzCheckout === "function") return Promise.resolve(window.EasebuzzCheckout);

  const existing = document.querySelector(`script[src="${EASEBUZZ_SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.EasebuzzCheckout), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Easebuzz checkout")), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = EASEBUZZ_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.EasebuzzCheckout);
    script.onerror = () => reject(new Error("Failed to load Easebuzz checkout"));
    document.body.appendChild(script);
  });
}

async function openEasebuzzCheckout(merchantKey, accessKey) {
  const EasebuzzCheckout = await loadEasebuzzScript();

  if (typeof EasebuzzCheckout !== "function") {
    throw new Error("Payment gateway could not be loaded. Please try again.");
  }

  const currentUrl = window.location.href;
  const environment =
    currentUrl.includes("localhost") ||
    currentUrl.includes("127.0.0.1") ||
    currentUrl.includes("app-qa.bhimanakattemath.org")
      ? "test"
      : "prod";

  return new Promise((resolve, reject) => {
    try {
      const easebuzzCheckout = new EasebuzzCheckout(merchantKey, environment);
      easebuzzCheckout.initiatePayment({
        access_key: accessKey,
        theme: "#123456",
        onResponse: (response) => {
          const status = response?.status?.toLowerCase();
          if (status === "success") {
            resolve(response);
            return;
          }
          reject(new Error("Payment was cancelled or could not be completed."));
        },
      });
    } catch (error) {
      reject(error);
    }
  });
}

function buildExecutionDate(dateValue) {
  if (!dateValue) return Date.now();
  const parsedDate = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(parsedDate.getTime()) ? Date.now() : parsedDate.getTime();
}

export default function SevaBooking() {
  const { sevaId } = useParams();
  const navigate = useNavigate();
  const { t, lv } = useLang();
  const b = t.booking;

  const [seva, setSeva] = useState(null);
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [sankalpaOptions, setSankalpaOptions] = useState({
    gothras: [],
    nakshatras: [],
    zodiacSigns: [],
  });
  const [isLoadingSankalpaOptions, setIsLoadingSankalpaOptions] = useState(true);
  const dateInputRef = useRef(null);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today,
    devotee_name: "",
    devotee_phone: "",
    devotee_address: "",
    gothraId: "",
    nakshatra: "",
    nakshatraId: "",
    rashi: "",
    zodiacSignId: "",
    gotra: "",
  });
  const [errors, setErrors] = useState({
    devotee_name: "",
    devotee_phone: "",
    devotee_address: "",
  });
  const [touched, setTouched] = useState({
    devotee_name: false,
    devotee_phone: false,
    devotee_address: false,
  });

  useEffect(() => {
    fetchSevaById(sevaId).then(data => {
      if (!data) throw new Error("Seva not found");
      setSeva(data);
    }).catch(() => {
      toast.error("Seva not found");
      navigate("/sevas");
    });
  }, [sevaId, navigate]);

  useEffect(() => {
    let isActive = true;

    async function loadSankalpaOptions() {
      setIsLoadingSankalpaOptions(true);

      const [gothrasResult, nakshatrasResult, zodiacSignsResult] = await Promise.allSettled([
        fetchGothras(),
        fetchNakshatras(),
        fetchZodiacSigns(),
      ]);

      if (!isActive) return;

      setSankalpaOptions({
        gothras: gothrasResult.status === "fulfilled" ? gothrasResult.value : [],
        nakshatras: nakshatrasResult.status === "fulfilled" ? nakshatrasResult.value : [],
        zodiacSigns: zodiacSignsResult.status === "fulfilled" ? zodiacSignsResult.value : [],
      });

      if ([gothrasResult, nakshatrasResult, zodiacSignsResult].some((result) => result.status === "rejected")) {
        toast.error("Some sankalpa options could not be loaded. Please try again.");
      }

      setIsLoadingSankalpaOptions(false);
    }

    loadSankalpaOptions();

    return () => {
      isActive = false;
    };
  }, []);

  const set = (k) => (e) => {
    let nextValue = e.target.value;

    if (k === "devotee_phone") {
      nextValue = nextValue.replace(/\D/g, "").slice(0, DEVOTEE_PHONE_LENGTH);
    }

    if (k === "devotee_name") {
      nextValue = nextValue.slice(0, DEVOTEE_NAME_MAX_LENGTH);
    }

    if (k === "devotee_address") {
      nextValue = nextValue.slice(0, DEVOTEE_ADDRESS_MAX_LENGTH);
    }

    setForm((current) => ({ ...current, [k]: nextValue }));

    if (k in touched && touched[k]) {
      setErrors((current) => ({
        ...current,
        [k]: validateDevoteeField(k, nextValue, b),
      }));
    }
  };
  const validateAndTouchField = (field, value) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current) => ({
      ...current,
      [field]: validateDevoteeField(field, value, b),
    }));
  };
  const setSelectField = ({ valueKey, labelKey, options }) => (value) => {
    const selectedOption = options.find((option) => option.id === value);
    setForm((current) => ({
      ...current,
      [valueKey]: value,
      [labelKey]: selectedOption?.label || "",
    }));
  };

  const next = () => {
    if (step === 1 && !form.date) return toast.error(b.errDate);
    if (step === 2) {
      const devoteeErrors = validateDevoteeForm(form, b);
      setTouched({
        devotee_name: true,
        devotee_phone: true,
        devotee_address: true,
      });
      setErrors(devoteeErrors);
      if (hasDevoteeErrors(devoteeErrors))
        return toast.error(b.errDevotee);
    }
    setStep(s => Math.min(s + 1, STEP_KEYS.length - 1));
  };
  const prev = () => setStep(s => Math.max(s - 1, 0));
  const selectCls = "mt-2 h-12 w-full rounded-md border border-[#E5DAC7] bg-white px-3 py-2 text-sm text-[#2A2118] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#7B1818] disabled:cursor-not-allowed disabled:bg-[#F4EBDB] disabled:text-[#8a6a3a]";
  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;

    input.focus();
    if (typeof input.showPicker === "function") {
      input.showPicker();
    }
  };

  const confirm = async () => {
    setIsProcessingPayment(true);
    try {
      const primaryFundGroup = seva?.raw?.fundGroups?.[0];
      const sevaTypeId = String(seva.id);
      const fundGroupId = String(primaryFundGroup?.fundGroupId ?? primaryFundGroup?.fundGroupMasterId ?? 4);
      const amountValue = String(seva.amount ?? 0);
      const payload = {
        amount: seva.amount,
        sevaTypes: {
          [sevaTypeId]: {
            [fundGroupId]: amountValue,
          },
        },
        devoteeName: form.devotee_name,
        email: 'admin@bhimasethumunivrindamutt.com',
        phone: form.devotee_phone,
        remarks: "",
        paymentChannelId: 9,
        executionDate: buildExecutionDate(form.date),
        deliveryOption: 1,
        transactionReference: null,
        institutionReference: INSTITUTION_REFERENCE,
        sevaCategoryId: seva?.raw?.sevaCategory ?? seva?.raw?.sevaCategoryId ?? 1,
        zodiacSignId: form.zodiacSignId || null,
        nakshatraId: form.nakshatraId || null,
        gothraId: form.gothraId || null,
        gothra: form.gotra || null,
        address: form.devotee_address || "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        toPaymentInstrumentId: null,
        fromBankMasterId: null,
        panNumber: "",
        sourceType: 11,
      };

      const response = await createSevaPayment(payload);
      const paymentData = response?.data;
      const paymentSessionId = paymentData?.paymentSessionId;
      const paymentReference = paymentData?.reference;
      const merchantKey = paymentData?.key ?? paymentData?.merchantKey ?? paymentData?.merchant_key;
      const accessKey = paymentSessionId ?? paymentData?.accessKey ?? paymentData?.access_key;

      if (!paymentReference || !merchantKey || !accessKey) {
        throw new Error("Payment session could not be created. Please try again.");
      }

      await openEasebuzzCheckout(merchantKey, accessKey);

      const statusResponse = await fetchSevaPaymentStatus(paymentReference);
      const statusData = statusResponse?.data;
      const status = statusData?.status?.toLowerCase();

      if (statusResponse?.status !== 1 || !["success", "completed"].includes(status)) {
        throw new Error("Payment could not be verified. Please check the payment status before trying again.");
      }

      setBooking({
        ...statusData,
        booking_reference:
          statusData?.booking_reference ||
          statusData?.bookingReference ||
          statusData?.reference ||
          paymentReference,
      });
      setStep(STEP_KEYS.length - 1);
      toast.success(b.okBooked);
    } catch (err) {
      toast.error(err?.message || b.errBooking);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  if (!seva) return <div className="min-h-[60vh]" />;

  const labelCls = "text-[12px] uppercase tracking-[0.18em] text-[#8a6a3a]";

  return (
    <div data-testid="seva-booking-page">
      <PageHeader eyebrow={b.eyebrow} title={lv(seva, "name")} sub={lv(seva, "description")} />

      <section className="section-pad">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stepper */}
          <ol data-testid="booking-stepper" className="flex items-center justify-between mb-14 relative">
            <div className="absolute left-0 right-0 top-5 h-px bg-[#E5DAC7] -z-0" />
            {STEP_KEYS.map((key, i) => (
              <li key={key} className="relative z-10 flex flex-col items-center flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  i < step
                    ? "bg-[#7B1818] border-[#7B1818] text-[#FDFBF7]"
                    : i === step
                    ? "bg-[#D4AF37] border-[#D4AF37] text-white shadow-lg"
                    : "bg-white border-[#E5DAC7] text-[#8a6a3a]"
                }`}>
                  {i < step ? <Check size={16} /> : i + 1}
                </div>
                <span className={`mt-2 text-[11px] uppercase tracking-[0.15em] hidden sm:block ${
                  i === step ? "text-[#7B1818] font-semibold" : "text-[#8a6a3a]"
                }`}>{b.steps[key]}</span>
              </li>
            ))}
          </ol>

          <div className="card-soft p-8 lg:p-10">
            {step === 0 && (
              <div data-testid="step-seva">
                <div className="eyebrow">{b.selectedSeva}</div>
                <h2 className="mt-3 font-display text-3xl text-[#2A2118] leading-tight">{lv(seva, "name")}</h2>
                <p className="mt-4 text-[15px] text-[#5C4D42] leading-relaxed">{lv(seva, "description")}</p>
                <div className="mt-8 flex items-center justify-between rounded-2xl bg-[#F4EBDB]/60 p-6">
                  <div>
                    <div className="eyebrow">{b.amount}</div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <IndianRupee size={18} className="text-[#7B1818]" />
                      <span className="font-display text-4xl text-[#7B1818]">{seva.amount.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-date">
                <div className="eyebrow">{b.chooseDate}</div>
                <h2 className="mt-3 font-display text-3xl text-[#2A2118]">{b.dateTitle}</h2>
                <p className="mt-3 text-[14px] text-[#5C4D42]">{b.dateSub}</p>
                <div className="mt-8 max-w-xs">
                  <Label htmlFor="date" className={labelCls}>{b.preferredDate}</Label>
                  <div
                    className="relative mt-2"
                    onClick={openDatePicker}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openDatePicker();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <Input
                      ref={dateInputRef}
                      id="date" type="date" min={today}
                      data-testid="booking-date-input"
                      value={form.date} onChange={set("date")}
                      className="booking-date-input h-12 border-[#E5DAC7] bg-white pr-12 text-[#2A2118]"
                    />
                    <CalendarDays
                      size={18}
                      className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#2A2118]"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-devotee">
                <div className="eyebrow">{b.devoteeDetails}</div>
                <h2 className="mt-3 font-display text-3xl text-[#2A2118]">{b.devoteeTitle}</h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className={labelCls}>{b.fullName}</Label>
                    <Input
                      data-testid="devotee-name"
                      value={form.devotee_name}
                      onChange={set("devotee_name")}
                      onBlur={() => validateAndTouchField("devotee_name", form.devotee_name)}
                      className={`mt-2 h-12 ${errors.devotee_name ? "border-[#B42318] focus-visible:ring-[#B42318]" : "border-[#E5DAC7]"}`}
                      maxLength={DEVOTEE_NAME_MAX_LENGTH}
                      aria-invalid={Boolean(errors.devotee_name)}
                    />
                    {errors.devotee_name && (
                      <p className="mt-2 text-sm text-[#B42318]">{errors.devotee_name}</p>
                    )}
                  </div>
                  <div>
                    <Label className={labelCls}>{b.phone}</Label>
                    <Input
                      data-testid="devotee-phone"
                      value={form.devotee_phone}
                      onChange={set("devotee_phone")}
                      onBlur={() => validateAndTouchField("devotee_phone", form.devotee_phone)}
                      className={`mt-2 h-12 ${errors.devotee_phone ? "border-[#B42318] focus-visible:ring-[#B42318]" : "border-[#E5DAC7]"}`}
                      maxLength={DEVOTEE_PHONE_LENGTH}
                      inputMode="numeric"
                      aria-invalid={Boolean(errors.devotee_phone)}
                    />
                    {errors.devotee_phone && (
                      <p className="mt-2 text-sm text-[#B42318]">{errors.devotee_phone}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className={labelCls}>{b.address}</Label>
                    <Textarea
                      data-testid="devotee-address"
                      rows={3}
                      value={form.devotee_address}
                      onChange={set("devotee_address")}
                      onBlur={() => validateAndTouchField("devotee_address", form.devotee_address)}
                      className={`mt-2 ${errors.devotee_address ? "border-[#B42318] focus-visible:ring-[#B42318]" : "border-[#E5DAC7]"}`}
                      maxLength={DEVOTEE_ADDRESS_MAX_LENGTH}
                      aria-invalid={Boolean(errors.devotee_address)}
                    />
                    {errors.devotee_address && (
                      <p className="mt-2 text-sm text-[#B42318]">{errors.devotee_address}</p>
                    )}
                  </div>
                  {/* <div className="sm:col-span-2 pt-2">
                    <div className="eyebrow">{b.sankalpaDetails}</div>
                    <p className="mt-3 text-[14px] text-[#5C4D42]">{b.sankalpaSub}</p>
                  </div> */}
                  <div>
                    <Label className={labelCls}>{b.gotra}</Label>
                    <select
                      data-testid="sankalpa-gotra"
                      className={selectCls}
                      value={form.gothraId}
                      onChange={(e) => setSelectField({
                        valueKey: "gothraId",
                        labelKey: "gotra",
                        options: sankalpaOptions.gothras,
                      })(e.target.value)}
                      disabled={isLoadingSankalpaOptions || sankalpaOptions.gothras.length === 0}
                    >
                      <option value="">
                        {isLoadingSankalpaOptions ? "Loading..." : `Select ${b.gotra}`}
                      </option>
                      {sankalpaOptions.gothras.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className={labelCls}>{b.nakshatra}</Label>
                    <select
                      data-testid="sankalpa-nakshatra"
                      className={selectCls}
                      value={form.nakshatraId}
                      onChange={(e) => setSelectField({
                        valueKey: "nakshatraId",
                        labelKey: "nakshatra",
                        options: sankalpaOptions.nakshatras,
                      })(e.target.value)}
                      disabled={isLoadingSankalpaOptions || sankalpaOptions.nakshatras.length === 0}
                    >
                      <option value="">
                        {isLoadingSankalpaOptions ? "Loading..." : `Select ${b.nakshatra}`}
                      </option>
                      {sankalpaOptions.nakshatras.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div >
                    <Label className={labelCls}>{b.rashi}</Label>
                    <select
                      data-testid="sankalpa-rashi"
                      className={selectCls}
                      value={form.zodiacSignId}
                      onChange={(e) => setSelectField({
                        valueKey: "zodiacSignId",
                        labelKey: "rashi",
                        options: sankalpaOptions.zodiacSigns,
                      })(e.target.value)}
                      disabled={isLoadingSankalpaOptions || sankalpaOptions.zodiacSigns.length === 0}
                    >
                      <option value="">
                        {isLoadingSankalpaOptions ? "Loading..." : `Select ${b.rashi}`}
                      </option>
                      {sankalpaOptions.zodiacSigns.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="sm:col-span-2">
                    <Label className={labelCls}>{b.note}</Label>
                    <Textarea data-testid="sankalpa-notes" rows={3} value={form.sankalpa_notes} onChange={set("sankalpa_notes")} className="mt-2 border-[#E5DAC7]" />
                  </div> */}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-payment">
                <div className="eyebrow">{b.payment}</div>
                <h2 className="mt-3 font-display text-3xl text-[#2A2118]">{b.paymentTitle}</h2>
                {/* <p className="mt-3 text-[14px] text-[#5C4D42]">
                  {MATHA.bankDetails.note}
                </p> */}

                <div className="mt-8 rounded-2xl bg-[#F4EBDB]/60 border border-[#E5DAC7] p-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="eyebrow">{b.seva}</div>
                      <div className="mt-1 font-display text-xl text-[#2A2118]">{lv(seva, "name")}</div>
                    </div>
                    <div>
                      <div className="eyebrow">{b.amountLabel}</div>
                      <div className="mt-1 font-display text-2xl text-[#7B1818] flex items-baseline gap-1">
                        <IndianRupee size={16} /> {seva.amount.toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div>
                      <div className="eyebrow">{b.date}</div>
                      <div className="mt-1 text-[15px]">{form.date}</div>
                    </div>
                    <div>
                      <div className="eyebrow">{b.devotee}</div>
                      <div className="mt-1 text-[15px]">{form.devotee_name}</div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-[#E5DAC7]">
                    {/* <div className="eyebrow">{b.upiBank}</div>
                    <div className="mt-2 text-[14px] text-[#5C4D42]">
                      <div>{b.accountName} <span className="font-semibold text-[#2A2118]">{MATHA.bankDetails.accountName}</span></div>
                      <div className="mt-1">{b.upi} <span className="font-semibold text-[#2A2118]">{MATHA.bankDetails.upi}</span></div>
                      <div className="mt-1">{MATHA.bankDetails.bank}</div>
                    </div> */}
                    <p className="mt-6 text-[13px] text-[#8a6a3a]">
                      Upon Confirmation you will be re-directed to Payment Gateway for completing the payment
                    </p>
                  </div>
                </div>

                {/* <p className="mt-6 text-[13px] text-[#8a6a3a]">
                  {b.confirmNote}
                </p> */}
              </div>
            )}

            {step === 4 && booking && (
              <div data-testid="step-done" className="text-center py-4">
                <div className="w-20 h-20 rounded-full bg-[#F4EBDB] mx-auto flex items-center justify-center">
                  <CheckCircle2 className="text-[#7B1818]" size={38} />
                </div>
                <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#2A2118]">{b.doneTitle}</h2>
                {/* <p className="mt-4 text-[15px] text-[#5C4D42] max-w-md mx-auto leading-relaxed">
                  {b.doneText}
                </p> */}
                <div className="mt-8 inline-flex items-center gap-3 bg-[#F4EBDB]/60 border border-[#E5DAC7] px-5 py-3 rounded-full">
                  <span className="eyebrow">{b.bookingRef}</span>
                  <span data-testid="booking-reference" className="text-xl text-[#7B1818]">{booking.booking_reference}</span>
                  <button
                    onClick={async () => {
                      try {
                        if (navigator.clipboard?.writeText) {
                          await navigator.clipboard.writeText(booking.booking_reference);
                        } else {
                          const ta = document.createElement("textarea");
                          ta.value = booking.booking_reference;
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand("copy");
                          document.body.removeChild(ta);
                        }
                        toast.success(b.copied);
                      } catch {
                        toast.error("Could not copy — please select and copy manually.");
                      }
                    }}
                    className="text-[#7B1818] hover:opacity-80"
                    data-testid="copy-booking-ref"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="mt-10 flex flex-wrap justify-center gap-3">
                  <Link to="/sevas" data-testid="done-more-sevas" className="btn-outline">{b.bookAnother}</Link>
                  <Link to="/" data-testid="done-home" className="btn-primary">{b.returnHome}</Link>
                </div>
              </div>
            )}
          </div>

          {step < 3 && (
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={prev}
                disabled={step === 0}
                data-testid="booking-prev-btn"
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> {t.common.back}
              </button>
              <button onClick={next} data-testid="booking-next-btn" className="btn-primary">
                {t.common.cont} <ArrowRight size={14} />
              </button>
            </div>
          )}
          {step === 3 && (
            <div className="mt-8 flex items-center justify-between">
              <button onClick={prev} data-testid="booking-prev-btn" className="btn-ghost">
                <ArrowLeft size={14} /> {t.common.back}
              </button>
              <button
                onClick={confirm}
                disabled={isProcessingPayment}
                data-testid="booking-confirm-btn"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isProcessingPayment ? "Processing..." : b.confirmBtn} <Check size={14} />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
